class GiftNote extends HTMLElement {
  static separator = "\n---\n";
  static header = "CADEAU : OUI";
  // Délai minimum entre la commande et la livraison, en jours.
  // Passer à 3 ou 1 ici suffit à changer la règle sur tout le site.
  static minLeadDays = 2;

  connectedCallback() {
    if (this.dataset.giftInit === "1") return;
    this.dataset.giftInit = "1";

    this.nameEl = this.querySelector('[data-gift="name"]');
    this.phoneEl = this.querySelector('[data-gift="phone"]');
    this.deliveryDateEl = this.querySelector('[data-gift="delivery-date"]');
    this.msgEl = this.querySelector('[data-gift="message"]');
    this.previewEl = this.querySelector('[data-gift="preview"]');
    this.previewWrapperEl = this.querySelector('[data-gift="preview-wrapper"]');
    this.errorEl = this.querySelector('[data-gift="error"]');

    this.noteEl = document.getElementById("Cart-note");
    this.checkoutButton = document.getElementById("checkout");

    if (!this.noteEl) return;

    this.setMinDate();
    this.hydrateFromNote();
    this.refreshUI();

    this.onFieldInput = async () => {
      this.refreshUI();
      await this.saveNote();
    };

    this.onFieldBlur = async () => {
      this.refreshUI();
      await this.saveNote();
    };

    [this.nameEl, this.phoneEl, this.deliveryDateEl, this.msgEl].forEach((el) => {
      if (!el) return;
      el.addEventListener("input", this.onFieldInput);
      el.addEventListener("blur", this.onFieldBlur);
    });
  }

  disconnectedCallback() {
    [this.nameEl, this.phoneEl, this.deliveryDateEl, this.msgEl].forEach((el) => {
      if (!el) return;
      el.removeEventListener("input", this.onFieldInput);
      el.removeEventListener("blur", this.onFieldBlur);
    });
  }

  setMinDate() {
    if (!this.deliveryDateEl) return;

    const earliest = new Date();
    earliest.setHours(0, 0, 0, 0);
    earliest.setDate(earliest.getDate() + GiftNote.minLeadDays);

    const yyyy = earliest.getFullYear();
    const mm = String(earliest.getMonth() + 1).padStart(2, "0");
    const dd = String(earliest.getDate()).padStart(2, "0");

    this.minDate = `${yyyy}-${mm}-${dd}`;
    this.deliveryDateEl.min = this.minDate;

    // Affiche la première date possible en clair sous le champ.
    const hintEl = this.querySelector('[data-gift="date-hint"]');
    if (hintEl) {
      hintEl.textContent = `Première date possible : ${earliest.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long"
      })}.`;
    }
  }

  extractNoteParts(note) {
    const content = (note || "").trim();

    const giftRegex = /(?:^|\n---\n)?CADEAU\s*:\s*(?:OUI|NON)[\s\S]*$/i;
    const match = content.match(giftRegex);

    const giftPart = match ? match[0].replace(/^\n---\n/, "").trim() : "";
    const base = content.replace(giftRegex, "").trim();

    return { base, giftPart };
  }

  parseGiftPart(giftPart) {
    const out = {
      hasGiftBlock: false,
      name: "",
      phone: "",
      deliveryDate: "",
      message: ""
    };

    if (!giftPart) return out;

    const normalized = giftPart.trim();
    if (!normalized.startsWith("CADEAU :")) return out;

    out.hasGiftBlock = true;

    const lines = normalized
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);

    const findVal = (prefix) => {
      const line = lines.find((l) => l.toLowerCase().startsWith(prefix.toLowerCase()));
      if (!line) return "";
      const idx = line.indexOf(":");
      if (idx === -1) return "";
      const val = line.slice(idx + 1).trim();
      return val === "-" ? "" : val;
    };

    out.name = findVal("Destinataire");
    out.phone = findVal("Téléphone");
    out.deliveryDate = findVal("Date de livraison");
    out.message = findVal("Message");

    return out;
  }

  hydrateFromNote() {
    const { giftPart } = this.extractNoteParts(this.noteEl.value);
    const parsed = this.parseGiftPart(giftPart);

    if (this.nameEl) this.nameEl.value = parsed.name || "";
    if (this.phoneEl) this.phoneEl.value = parsed.phone || "";
    if (this.msgEl) this.msgEl.value = parsed.message || "";

    // Une date enregistrée avant le délai minimum (panier abandonné puis repris)
    // est écartée plutôt que réaffichée comme si elle était valable.
    if (this.deliveryDateEl) {
      const saved = parsed.deliveryDate || "";
      this.deliveryDateEl.value = saved && this.minDate && saved < this.minDate ? "" : saved;
    }
  }

  getName() {
    return (this.nameEl?.value || "").trim();
  }

  getPhone() {
    return (this.phoneEl?.value || "").trim();
  }

  getDeliveryDate() {
    return (this.deliveryDateEl?.value || "").trim();
  }

  getMessage() {
    return (this.msgEl?.value || "").trim();
  }

  isPhoneValid() {
    return this.getPhone().length > 0;
  }

  isDeliveryDateValid() {
    const value = this.getDeliveryDate();
    if (!value) return false;
    if (this.deliveryDateEl?.min && value < this.deliveryDateEl.min) return false;
    return true;
  }

  validateRequiredFields() {
    return this.isPhoneValid() && this.isDeliveryDateValid();
  }

  buildGiftBlock() {
    const name = this.getName() || "-";
    const phone = this.getPhone() || "-";
    const deliveryDate = this.getDeliveryDate() || "-";
    const msg = this.getMessage() || "-";

    return [
      GiftNote.header,
      `Destinataire : ${name}`,
      `Téléphone : ${phone}`,
      `Date de livraison : ${deliveryDate}`,
      `Message : ${msg}`
    ].join("\n");
  }

  composeNote() {
    const { base } = this.extractNoteParts(this.noteEl.value);
    const giftBlock = this.buildGiftBlock();
    return base ? `${base}${GiftNote.separator}${giftBlock}` : giftBlock;
  }

  buildPreview() {
    const lines = [
      `Destinataire : ${this.getName() || "-"}`,
      `Téléphone : ${this.getPhone() || "-"}`,
      `Date de livraison : ${this.getDeliveryDate() || "-"}`,
      `Message : ${this.getMessage() || "-"}`
    ];
    return lines.join("\n");
  }

  refreshUI() {
    const isValid = this.validateRequiredFields();

    if (this.checkoutButton) {
      this.checkoutButton.disabled = !isValid;
      this.checkoutButton.setAttribute("aria-disabled", (!isValid).toString());
    }

    if (this.errorEl) {
      this.errorEl.classList.toggle("is-visible", !isValid);
    }

    const hasAnyContent =
      this.getName() || this.getPhone() || this.getDeliveryDate() || this.getMessage();

    if (this.previewWrapperEl) {
      this.previewWrapperEl.style.display = hasAnyContent ? "block" : "none";
    }

    if (this.previewEl) {
      this.previewEl.textContent = this.buildPreview();
    }
  }

  async saveNote() {
    const newNote = this.composeNote();
    this.noteEl.value = newNote;

    await fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: newNote })
    });
  }
}

if (!customElements.get("gift-note")) {
  customElements.define("gift-note", GiftNote);
}
